import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { businessAPI, reportsAPI } from '../../services/apiServices';
import { useFetch } from '../../hooks/useFetch';
import { Alert, EmptyState, Spinner } from '../../components/common/UI';
import { useFeedback } from '../../contexts/FeedbackContext';
import {
  BriefcaseIcon,
  BusinessCardIcon,
  CalendarIcon,
  EditIcon,
  FilterIcon,
  MoreVerticalIcon,
  PlusIcon,
  SearchIcon,
  TrashIcon,
  UserIcon,
} from '../../components/common/Icons';
import QrCodeModal from './QrCodeModal';
import { getDerivedBusinessStatus, PROGRESS_STATUS_KEYS } from '../../utils/businessStatus';
import {
  getSubscriptionRegularizationMessage,
  isSubscriptionLock,
} from '../../utils/plans';
import { formatLocalizedDate } from '../../utils/dateUtils';
import { useAuth } from '../../contexts/AuthContext';
import { getBusinessClientName, getProfileOwnerName } from '../../utils/businessOwner';
import MoneyValue from '../../components/common/MoneyValue';

const STATUS_CLASSES = {
  pre_initial: 'bg-slate-100 text-slate-700',
  started: 'bg-sky-50 text-sky-700',
  in_progress: 'bg-emerald-50 text-emerald-700',
  paused: 'bg-amber-50 text-amber-700',
  aborted: 'bg-rose-50 text-rose-700',
  canceled: 'bg-red-50 text-red-700',
  completed: 'bg-emerald-100 text-emerald-800',
};

const fetchBusinessList = async () => {
  const [businessesRes, progressRes] = await Promise.all([
    businessAPI.getAll(),
    reportsAPI.progress(),
  ]);

  return {
    data: {
      businesses: businessesRes.data?.data || businessesRes.data || [],
      progress: progressRes.data?.data || progressRes.data || [],
    },
  };
};

const getBusinessBalance = (business) => {
  if (business.balance !== undefined) return Number(business.balance || 0);
  return (business.financialEntries || []).reduce((sum, entry) => {
    const amount = Number(entry.amount || 0);
    return entry.type === 'debit' ? sum - amount : sum + amount;
  }, 0);
};

const recordTime = (value) => {
  const time = new Date(value || 0).getTime();
  return Number.isNaN(time) ? 0 : time;
};

const sortByCreatedDesc = (items = []) => (
  [...items].sort((a, b) => recordTime(b.createdAt || b.updatedAt || b.startDate) - recordTime(a.createdAt || a.updatedAt || a.startDate))
);

const BusinessListPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { confirm, notify } = useFeedback();
  const { user } = useAuth();
  const { data, loading, error, refetch } = useFetch(fetchBusinessList, []);
  const [qrOpen, setQrOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [actionBusiness, setActionBusiness] = useState(null);

  const businesses = useMemo(() => data?.businesses || [], [data?.businesses]);
  const progressByBusiness = useMemo(() => (
    new Map((data?.progress || []).map((business) => [
      business.id,
      getDerivedBusinessStatus(business.progressList),
    ]))
  ), [data]);

  const orderedBusinesses = useMemo(() => sortByCreatedDesc(businesses), [businesses]);
  const businessTypes = useMemo(() => [...new Set(businesses.map((business) => business.businessType).filter(Boolean))], [businesses]);
  const businessTypeLabel = (type) => t(`businessTypes.${type}`, { defaultValue: type });

  const handleLockedBusinessClick = (event, business) => {
    if (!business.access?.locked) return;
    event.preventDefault();
    const subscriptionLocked = isSubscriptionLock(business.access);
    const subscription = business.access?.subscription || {};
    const title = subscriptionLocked ? t('plans.regularizeTitle') : t('plans.planLimitTitle');
    const message = subscriptionLocked
      ? getSubscriptionRegularizationMessage(subscription, t, business.access?.plan)
      : t('plans.planLimitMessage', { limit: business.access.businessLimit });
    const confirmText = subscriptionLocked ? t('plans.regularizeCta') : t('plans.upgradeToStandard');
    notify({ variant: 'warning', title, message, confirmText }).then(() => navigate('/subscription'));
  };

  const toggleBusinessActions = (event, business) => {
    event.preventDefault();
    event.stopPropagation();
    setActionBusiness((current) => (current?.id === business.id ? null : business));
  };

  const handleEditBusiness = (event, business) => {
    event.preventDefault();
    event.stopPropagation();
    setActionBusiness(null);
    navigate(`/businesses/${business.id}/edit`);
  };

  const handleDeleteBusiness = async (event, business) => {
    event.preventDefault();
    event.stopPropagation();
    setActionBusiness(null);
    const ok = await confirm({
      variant: 'danger',
      title: t('businessDetails.deleteTitle'),
      message: t('businessDetails.deleteMessage'),
      confirmText: t('common.delete'),
      cancelText: t('common.cancel'),
    });
    if (!ok) return;
    try {
      await businessAPI.remove(business.id);
      await refetch();
    } catch {
      notify({ variant: 'danger', title: t('businessDetails.deleteErrorTitle'), message: t('businessDetails.deleteError') });
    }
  };

  const filtered = orderedBusinesses.filter((business) => {
    const needle = search.toLowerCase();
    const status = progressByBusiness.get(business.id) || getDerivedBusinessStatus(business.progressList);
    const ownerName = getProfileOwnerName(user, t);
    return (
      (String(business.name || '').toLowerCase().includes(needle) ||
        String(business.client?.name || ownerName).toLowerCase().includes(needle) ||
        String(business.businessType || '').toLowerCase().includes(needle)) &&
      (statusFilter === 'all' || status === statusFilter) &&
      (typeFilter === 'all' || business.businessType === typeFilter)
    );
  });

  if (loading) return <Spinner size="lg" />;

  return (
    <div className="mx-auto max-w-[1104px]">
      <div className="mb-7 flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">{t('navigation.businesses')}</h1>
          <p className="mt-1 text-base text-slate-600">{t('businesses.subtitle')}</p>
        </div>
        <Link
          to="/businesses/new"
          className="inline-flex h-10 items-center justify-center gap-3 rounded-lg bg-sky-500 px-6 text-sm font-bold text-white shadow-soft transition hover:bg-sky-600"
        >
          <PlusIcon className="h-4 w-4" />
          {t('businesses.new')}
        </Link>
      </div>

      {error && <Alert variant="error">{error}</Alert>}

      <div className="mb-6 flex flex-col gap-4 md:flex-row">
        <label className="relative flex-1">
          <SearchIcon className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-slate-500" />
          <input
            className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-12 pr-4 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-500 focus:border-sky-300 focus:ring-4 focus:ring-sky-100"
            placeholder={t('businesses.searchPlaceholder')}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <button
          className="inline-flex h-10 items-center justify-center gap-3 rounded-lg border border-slate-200 bg-white px-6 text-sm font-bold text-slate-900 shadow-sm transition hover:border-sky-200 hover:text-sky-600"
          type="button"
          onClick={() => setFiltersOpen((value) => !value)}
        >
          <FilterIcon className="h-4 w-4" />
          {t('actions.filter')}
        </button>
      </div>

      {filtersOpen && (
        <section className="mb-6 grid gap-3 rounded-lg border border-slate-200 bg-white p-4 shadow-soft md:grid-cols-3">
          <label className="text-xs font-bold text-slate-600">
            {t('filters.status')}
            <select className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm" value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
              {['all', ...PROGRESS_STATUS_KEYS].map((status) => (
                <option key={status} value={status}>{status === 'all' ? t('filters.all') : t(`status.${status}`)}</option>
              ))}
            </select>
          </label>
          <label className="text-xs font-bold text-slate-600">
            {t('filters.businessType')}
            <select className="mt-1 h-10 w-full rounded-lg border border-slate-200 px-3 text-sm" value={typeFilter} onChange={(event) => setTypeFilter(event.target.value)}>
              <option value="all">{t('filters.all')}</option>
              {businessTypes.map((type) => <option key={type} value={type}>{businessTypeLabel(type)}</option>)}
            </select>
          </label>
          <button
            className="mt-5 h-10 rounded-lg border border-slate-200 bg-slate-50 px-4 text-sm font-bold text-slate-700"
            type="button"
            onClick={() => { setStatusFilter('all'); setTypeFilter('all'); }}
          >
            {t('filters.clear')}
          </button>
        </section>
      )}

      {filtered.length === 0 ? (
        <EmptyState
          icon=""
          title={t('businesses.emptyTitle')}
          description={t('businesses.emptyDescription')}
          action={
            <Link
              to="/businesses/new"
              className="inline-flex h-10 items-center rounded-lg bg-sky-500 px-4 text-sm font-bold text-white"
            >
              {t('businesses.create')}
            </Link>
          }
        />
      ) : (
        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {filtered.map((business) => {
            const status = progressByBusiness.get(business.id) || getDerivedBusinessStatus(business.progressList);
            const dateRange = business.endDate
              ? `${formatLocalizedDate(business.startDate, i18n.language)} - ${formatLocalizedDate(business.endDate, i18n.language)}`
              : formatLocalizedDate(business.startDate, i18n.language);

            const subscriptionLocked = isSubscriptionLock(business.access);
            const lockTitle = subscriptionLocked ? t('plans.regularizeTitle') : t('plans.planLimitTitle');
            const lockMessage = subscriptionLocked
              ? getSubscriptionRegularizationMessage(business.access?.subscription || {}, t, business.access?.plan)
              : t('plans.planLimitMessage', { limit: business.access?.businessLimit });
            const lockCta = subscriptionLocked ? t('plans.regularizeCta') : t('plans.upgradeToStandard');

            return (
              <Link
                to={business.access?.locked ? '/subscription' : `/businesses/${business.id}`}
                key={business.id}
                onClick={(event) => handleLockedBusinessClick(event, business)}
                className={`group relative rounded-lg border border-slate-200 bg-white p-6 shadow-soft transition hover:-translate-y-0.5 hover:shadow-lg ${business.access?.locked ? 'overflow-hidden' : ''}`}
              >
                <div className={business.access?.locked ? 'pointer-events-none blur-[2px]' : ''}>
                <div className="mb-5 flex items-start justify-between gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sky-50 text-sky-500">
                    <BriefcaseIcon className="h-6 w-6" />
                  </div>
                  {!business.access?.locked && (
                    <button
                      className="rounded-full p-1 text-slate-600 transition hover:bg-slate-100"
                      type="button"
                      onClick={(event) => toggleBusinessActions(event, business)}
                      aria-label={t('actions.moreOptions')}
                    >
                      <MoreVerticalIcon className="h-5 w-5" />
                    </button>
                  )}
                </div>

                {actionBusiness?.id === business.id && (
                  <div
                    className="absolute right-5 top-14 z-10 w-40 overflow-hidden rounded-lg border border-slate-200 bg-white py-1 shadow-lg"
                    onClick={(event) => event.stopPropagation()}
                  >
                    <button
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
                      type="button"
                      onClick={(event) => handleEditBusiness(event, business)}
                    >
                      <EditIcon className="h-4 w-4" />
                      {t('common.edit')}
                    </button>
                    <button
                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm font-semibold text-red-500 transition hover:bg-red-50"
                      type="button"
                      onClick={(event) => handleDeleteBusiness(event, business)}
                    >
                      <TrashIcon className="h-4 w-4" />
                      {t('common.delete')}
                    </button>
                  </div>
                )}

                <h2 className="line-clamp-2 text-lg font-bold leading-snug text-slate-900">
                  {business.name}
                </h2>

                <div className="mt-3 space-y-2 text-sm text-slate-600">
                  <div className="flex items-center gap-2">
                    <UserIcon className="h-4 w-4 shrink-0 text-slate-700" />
                    <span className="truncate">{getBusinessClientName(business, user, t)}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <CalendarIcon className="h-4 w-4 shrink-0 text-slate-700" />
                    <span>{dateRange}</span>
                  </div>
                  <div className="flex items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2">
                    <span className="text-xs font-bold text-slate-600">{t('reports.balance')}</span>
                    <span className={`text-sm font-black ${getBusinessBalance(business) < 0 ? 'text-rose-600' : 'text-emerald-700'}`}>
                      <MoneyValue value={getBusinessBalance(business)} />
                    </span>
                  </div>
                </div>

                <div className="mt-5 flex items-center justify-between gap-3">
                  <span className="max-w-[50%] truncate rounded-full bg-slate-200 px-3 py-1 text-xs font-medium text-slate-700">
                    {business.businessType ? businessTypeLabel(business.businessType) : t('businesses.noCategory')}
                  </span>
                  {status && (
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${STATUS_CLASSES[status] || 'bg-slate-100 text-slate-600'}`}>
                      {t(`status.${status}`, status)}
                    </span>
                  )}
                </div>
                </div>
                {business.access?.locked && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/75 p-5 text-center backdrop-blur-[1px]">
                    <p className="text-sm font-bold text-slate-900">{lockTitle}</p>
                    <p className="mt-2 text-xs text-slate-600">{lockMessage}</p>
                    <span className="mt-3 rounded-lg bg-sky-500 px-4 py-2 text-xs font-bold text-white">{lockCta}</span>
                  </div>
                )}
              </Link>
            );
          })}
        </div>
      )}

      <button
        className="fixed bottom-5 right-5 inline-flex h-12 w-12 items-center justify-center rounded-full bg-white text-sky-500 shadow-soft ring-1 ring-slate-200 transition hover:bg-sky-50 sm:hidden"
        onClick={() => setQrOpen(true)}
        aria-label={t('actions.generateQrCode')}
      >
        <BusinessCardIcon className="h-5 w-5" />
      </button>
      <QrCodeModal isOpen={qrOpen} onClose={() => setQrOpen(false)} />
    </div>
  );
};

export default BusinessListPage;
